"use client";

/**
 * Step 3: Customer Information
 * Simple form with only essential fields
 */

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import type { CustomerData } from "@/types/booking-v2";
import { Mail, MessageSquare, Phone, User } from "lucide-react";

interface CustomerStepProps {
  data: CustomerData;
  onChange: (data: CustomerData) => void;
}

export function CustomerStep({ data, onChange }: CustomerStepProps) {
  const formatPhone = (value: string) => {
    const numbers = value.replace(/\D/g, "");
    if (numbers.length <= 10) {
      return numbers.replace(/(\d{2})(\d{4})(\d{0,4})/, "($1) $2-$3");
    }
    return numbers.replace(/(\d{2})(\d{5})(\d{0,4})/, "($1) $2-$3");
  };

  const handlePhoneChange = (value: string) => {
    onChange({ ...data, phone: formatPhone(value) });
  };

  return (
    <div className="space-y-5 h-full flex flex-col">
      {/* Compact info banner */}
      <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg p-3 shrink-0">
        <p className="text-sm text-blue-900 dark:text-blue-100">
          📱 Seus dados estão seguros - Usaremos apenas para confirmar seu
          agendamento
        </p>
      </div>

      {/* Scrollable form */}
      <div className="flex-1 overflow-y-auto -mx-4 md:mx-0 px-4 md:px-0 overscroll-contain">
        <div className="space-y-5 pb-4">
          {/* Name */}
          <div className="space-y-2">
            <Label htmlFor="name" className="text-base font-semibold">
              Nome Completo *
            </Label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
              <Input
                id="name"
                type="text"
                placeholder="Digite seu nome"
                value={data.name}
                onChange={(e) => onChange({ ...data, name: e.target.value })}
                className="pl-10 h-12 text-base"
                required
              />
            </div>
            {data.name.length > 0 && data.name.length < 2 && (
              <p className="text-xs text-destructive">
                Nome deve ter pelo menos 2 caracteres
              </p>
            )}
          </div>

          {/* Phone */}
          <div className="space-y-2">
            <Label htmlFor="phone" className="text-base font-semibold">
              Telefone/WhatsApp *
            </Label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
              <Input
                id="phone"
                type="tel"
                placeholder="(00) 00000-0000"
                value={data.phone}
                onChange={(e) => handlePhoneChange(e.target.value)}
                className="pl-10 h-12 text-base"
                maxLength={15}
                required
              />
            </div>
            {data.phone.length > 0 &&
              data.phone.replace(/\D/g, "").length < 8 && (
                <p className="text-xs text-destructive">Telefone inválido</p>
              )}
          </div>

          {/* Email (optional) */}
          <div className="space-y-2">
            <Label htmlFor="email" className="text-base font-semibold">
              E-mail{" "}
              <span className="text-muted-foreground text-sm">(opcional)</span>
            </Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
              <Input
                id="email"
                type="email"
                placeholder="seu@email.com"
                value={data.email}
                onChange={(e) => onChange({ ...data, email: e.target.value })}
                className="pl-10 h-12 text-base"
              />
            </div>
          </div>

          {/* Notes (optional) */}
          <div className="space-y-2">
            <Label htmlFor="notes" className="text-base font-semibold">
              Observações{" "}
              <span className="text-muted-foreground text-sm">(opcional)</span>
            </Label>
            <div className="relative">
              <MessageSquare className="absolute left-3 top-3 h-5 w-5 text-muted-foreground" />
              <Textarea
                id="notes"
                placeholder="Alguma informação adicional que queira nos passar?"
                value={data.notes}
                onChange={(e) => onChange({ ...data, notes: e.target.value })}
                className="pl-10 min-h-[100px] text-base resize-none"
                maxLength={500}
              />
            </div>
            {data.notes && (
              <p className="text-xs text-muted-foreground text-right">
                {data.notes.length}/500 caracteres
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
